'use client';

import { formatDayLong, fromIsoDate, toIsoDate } from '@/lib/date';
import { Button, Calendar, Spinner } from '@holistic/components';
import { RotateCw } from '@holistic/components/icons';

import type { PickupDates } from './use-pickup-dates';

// Takvimin Türkçe başlık/gün adları. date-fns locale'i doğrudan bağımlılığımız
// olmadığı için Intl üzerinden veriliyor.
const CALENDAR_FORMATTERS = {
  formatCaption: (date: Date) =>
    date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' }),
  formatWeekdayName: (date: Date) =>
    date.toLocaleDateString('tr-TR', { weekday: 'short' }),
};

type PickupDateFieldProps = {
  dates: PickupDates;
  value: string | null;
  error?: string;
  onSelect: (date: string | null) => void;
};

/**
 * Randevu takvimi. Yalnızca adres tamamlandığında gösterilir ve yalnızca
 * backend'in döndüğü günleri açar (bkz. use-pickup-dates.ts).
 */
const PickupDateField = ({
  dates,
  value,
  error,
  onSelect,
}: PickupDateFieldProps) => {
  if (dates.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  // Takvim hiç gelmediyse (hata) ya da ilçe için gün paylaşılmadıysa (boş
  // liste) seçtirecek gün yok. Gün seçilmeden ödeme açılmadığı için akış
  // burada duruyor; müşteriyi tekrar denemeye ya da adresi değiştirmeye
  // yönlendiriyoruz.
  if (dates.isError || !dates.hasDays) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm leading-relaxed text-gray-500">
          {dates.isError
            ? 'Randevu takvimi getirilemedi. Tekrar deneyin; sorun sürerse birkaç dakika sonra yeniden bakın.'
            : 'Bu ilçe için şu an randevu günü paylaşılmamış. Tekrar deneyebilir ya da teslim alma adresini değiştirebilirsiniz.'}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => dates.refetch()}
          className="rounded-full px-6"
        >
          <RotateCw className="mr-1.5 size-4" />
          Tekrar dene
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <p className="self-start text-xs leading-relaxed text-gray-400">
        Kurye yalnızca işaretli günlerde alım yapabiliyor.
      </p>

      <Calendar
        mode="single"
        selected={value ? fromIsoDate(value) : undefined}
        onSelect={(date?: Date) => onSelect(date ? toIsoDate(date) : null)}
        disabled={(date: Date) => !dates.isSelectable(toIsoDate(date))}
        defaultMonth={dates.firstDay ?? undefined}
        startMonth={dates.firstDay ?? undefined}
        endMonth={dates.lastDay ?? undefined}
        weekStartsOn={1}
        formatters={CALENDAR_FORMATTERS}
        className="p-0"
      />

      <p className="text-center text-sm">
        {value ? (
          <span className="font-semibold text-gray-900">
            {formatDayLong(value)}
          </span>
        ) : (
          <span className={error ? 'text-destructive' : 'text-gray-400'}>
            {error ?? 'Takvimden bir gün seçin'}
          </span>
        )}
      </p>
    </div>
  );
};

export default PickupDateField;
